import axios from "axios";
import { computed, ref, watch } from "vue";
import { getWorkspaceLocale } from "@/shared/infrastructure/adapters/workspaceLocaleService";
import { logger } from "@/infrastructure/services/LoggerService";
const FALLBACK_COUNTRY = "FR";
export const WORKSPACE_COUNTRY_RETRY_DELAYS_MS = [2000, 10000, 30000];
// One read per session, shared by every consumer: the OPUS region fixes the country at workspace
// creation.
const workspaceCountry = ref(FALLBACK_COUNTRY);
let pendingLoad = null;
let transientFailure = false;
function toWorkspaceCountry(country) {
    switch (country) {
        case "FR":
        case "NL":
        case "CA":
            return country;
        default:
            return FALLBACK_COUNTRY;
    }
}
// A 401/403 or a 4xx will answer the same on every attempt.
function isTransient(error) {
    if (!axios.isAxiosError(error))
        return false;
    return !error.response || error.response.status >= 500;
}
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
async function readWorkspaceCountry() {
    transientFailure = false;
    for (let attempt = 0;; attempt++) {
        try {
            const workspaceLocale = await getWorkspaceLocale();
            workspaceCountry.value = toWorkspaceCountry(workspaceLocale?.country);
            return;
        }
        catch (error) {
            const transient = isTransient(error);
            if (!transient || attempt === WORKSPACE_COUNTRY_RETRY_DELAYS_MS.length) {
                pendingLoad = null;
                transientFailure = transient;
                logger.error("useWorkspaceCountry: workspace locale read failed", error);
                return;
            }
            await wait(WORKSPACE_COUNTRY_RETRY_DELAYS_MS[attempt]);
        }
    }
}
/**
 * The workspace country. Setting up a consumer after a read gave up on a transient failure
 * (network, 5xx) reads again, so an outage at login does not pin FR for the whole session.
 */
export function useWorkspaceCountry() {
    if (transientFailure)
        void loadWorkspaceCountry();
    return computed(() => workspaceCountry.value);
}
/**
 * Reads the workspace country from the BFF; concurrent and later calls share that read. A
 * workspace with no country (EN region, no client) resolves to FR. A transient failure is retried
 * with a backoff; when the read gives up, the country stays as it was.
 */
export function loadWorkspaceCountry() {
    pendingLoad ?? (pendingLoad = readWorkspaceCountry());
    return pendingLoad;
}
/**
 * Starts {@link loadWorkspaceCountry} each time the session becomes authenticated. The read
 * resolves after forms may already be set up: a consumer that copies the country at setup must
 * also follow later changes (see useAddressForm).
 */
export function loadWorkspaceCountryOnAuthentication(isAuthenticated) {
    watch(isAuthenticated, (authenticated) => {
        if (authenticated)
            void loadWorkspaceCountry();
    }, { immediate: true });
}
export function setWorkspaceCountry(country) {
    workspaceCountry.value = country;
}
